import React from 'react';

import { W2_TID } from '../constants/testids';
import {
  W2_DONE_COMPLETE_COPY,
  W2_DONE_NOT_ENROLLED_COPY,
  W2_DONE_QUESTIONNAIRE_PENDING_COPY,
  W2_DONE_RETURNING_COPY,
  W2_DONE_SELECTION_CLOSED_COPY,
  W2_DONE_SELECTION_UNAVAILABLE_COPY,
  W2_DONE_WAIVE_COPY,
} from '../copy';
import { W2Shell } from './W2Shell';

/**
 * Why a W2 flow terminated. The first three values are the PHQ spec states;
 * the remaining values are honest, cause-specific authenticated-stage states:
 *   - `complete`  → screen 10 (fresh PHQ submission this session).
 *   - `waive`     → screen 3  (fresh waiver this session, "Thank You, {Firstname}!").
 *   - `returning` → lockout   (a completed/waived employee logged back in; the
 *                              enroll gate routed to done BEFORE the PHQ rendered).
 */
export type W2DoneReason =
  | 'complete'
  | 'waive'
  | 'returning'
  | 'questionnaire-pending'
  | 'selection-closed'
  | 'selection-unavailable'
  | 'not-enrolled';

interface W2DoneProps {
  reason?: W2DoneReason;
  /** First name for the waiver screen (screen 3); sourced from the account. */
  firstName?: string;
}

/**
 * Resolve the terminal headline + body for a given reason — the single source
 * shared by the standalone selection-stage card and the reskinned PHQ done
 * screen (`W2DoneScreen`).
 */
export function resolveW2DoneCopy(
  reason: W2DoneReason,
  firstName?: string
): { title: string; body: string } {
  switch (reason) {
    case 'waive':
      return {
        title: W2_DONE_WAIVE_COPY.headline(firstName),
        body: W2_DONE_WAIVE_COPY.body,
      };
    case 'returning':
      return {
        title: W2_DONE_RETURNING_COPY.headline,
        body: W2_DONE_RETURNING_COPY.body,
      };
    case 'questionnaire-pending':
      return {
        title: W2_DONE_QUESTIONNAIRE_PENDING_COPY.headline,
        body: W2_DONE_QUESTIONNAIRE_PENDING_COPY.body,
      };
    case 'selection-closed':
      return {
        title: W2_DONE_SELECTION_CLOSED_COPY.headline,
        body: W2_DONE_SELECTION_CLOSED_COPY.body,
      };
    case 'selection-unavailable':
      return {
        title: W2_DONE_SELECTION_UNAVAILABLE_COPY.headline,
        body: W2_DONE_SELECTION_UNAVAILABLE_COPY.body,
      };
    case 'not-enrolled':
      return {
        title: W2_DONE_NOT_ENROLLED_COPY.headline,
        body: W2_DONE_NOT_ENROLLED_COPY.body,
      };
    case 'complete':
      return {
        title: W2_DONE_COMPLETE_COPY.headline,
        body: W2_DONE_COMPLETE_COPY.body,
      };
  }
}

/** Terminal thank-you screen. Renders for terminal PHQ status (enforces the
 * immutability gate: done shows BEFORE the PHQ for a completed/waived employee). */
export const W2Done: React.FC<W2DoneProps> = ({
  reason = 'complete',
  firstName,
}) => {
  const { title, body } = resolveW2DoneCopy(reason, firstName);

  return (
    <W2Shell title={title} showLogout>
      <div data-testid={W2_TID.done.root} className="flex flex-col gap-4">
        <p
          data-testid={W2_TID.done.message}
          data-reason={reason}
          className="whitespace-pre-line text-[15px] leading-6 text-[#5a5a70]"
        >
          {body}
        </p>
      </div>
    </W2Shell>
  );
};
